import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getUser, logout } from 'pages/auth/actions';
import { userSelector } from 'pages/auth/selectors';
import { Toastify } from 'hooks';
import { isBrokerageAdmin } from 'helpers/checkUsersRoles';
import { scrollTopWithoutAnimation } from 'utills';
import { TOAST_TYPES } from 'constants';
import { TOAST_MESSAGES } from 'constants';

import { AgentNoImage } from 'components/agentNoImage';

// images
import PropertyLogo from 'assets/images/propertylogo.svg';
import Menu from 'assets/images/menu.svg';
import CloseIcon from 'assets/images/close.svg';
import LogoutIcon from 'assets/images/power.svg';

const base_url = process.env.REACT_APP_BASE_URL;
const image_base_url = process.env.REACT_APP_IMAGE_BASE_URL;

export default function DefaultLayout({ children }) {
  const [showSidebar, setShowSidebar] = useState(false);
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);

  const isDownloadZone = pathname.includes('/download-zone');

  const navigate = useNavigate();

  const handleLogout = () => {
    dispatch(logout())
      .then(() => {
        navigate('/sign-in');
      })
      .catch(() => {
        Toastify({
          type: TOAST_TYPES.error,
          message: TOAST_MESSAGES.tryAgain,
        });
      });
  };

  const handleToggleSidebar = () => {
    setShowSidebar((prevShowSidebar) => !prevShowSidebar);
  };

  const handleCloseSideBar = () => {
    setShowSidebar(false);
  };

  useEffect(() => {
    if (!Object.keys(user).length) {
      dispatch(getUser());
    }
  }, []);

  useEffect(() => {
    scrollTopWithoutAnimation();
  }, [pathname]);

  return (
    <>
      <div
        className={`headerMobile d-flex d-lg-none ${
          (isDownloadZone || pathname.includes('/property-website/')) &&
          'not-display'
        }`}
        id={`${isDownloadZone && 'not-display'}`}
      >
        <div className="brandname">
          <Link to="/">
            <img src={PropertyLogo} alt="logo" />
          </Link>
        </div>
        <button onClick={handleToggleSidebar} className="toggleSidebar">
          <img src={Menu} alt="menu" />
        </button>
      </div>
      <div className="main">
        {!pathname.includes('/property-website/') &&
          !pathname.includes('/download-zone/') && (
            <div
              className={`sidebar ${showSidebar ? 'showSidebar' : ''} ${
                isDownloadZone && 'not-display'
              }`}
              id="sidebar"
            >
              <div className={`${showSidebar ? 'mobileSidebarHeader' : ''}`}>
                <div className="brandname">
                  <Link to="/">
                    <img src={PropertyLogo} alt="logo" />
                  </Link>
                </div>
                {showSidebar && (
                  <div onClick={handleToggleSidebar}>
                    <img src={CloseIcon} alt="close" />
                  </div>
                )}
              </div>
              <ul className="mainLinks">
                <li onClick={handleCloseSideBar}>
                  <Link
                    to="/tours"
                    className={`cta-link ${
                      (pathname.includes('tour') ||
                        pathname.includes('property-website') ||
                        pathname.includes('choose-market') ||
                        pathname === '/') &&
                      'active'
                    }`}
                  >
                    <i>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.9">
                          <path
                            d="M12 7V3H2V21H22V7H12ZM6 19H4V17H6V19ZM6 15H4V13H6V15ZM6 11H4V9H6V11ZM6 7H4V5H6V7ZM10 19H8V17H10V19ZM10 15H8V13H10V15ZM10 11H8V9H10V11ZM10 7H8V5H10V7ZM20 19H12V17H14V15H12V13H14V11H12V9H20V19ZM18 11H16V13H18V11ZM18 15H16V17H18V15Z"
                            fill="#A8B0B9"
                          />
                        </g>
                      </svg>
                    </i>
                    <span>My Listings</span>
                  </Link>
                </li>
                <li
                  hidden={!isBrokerageAdmin(user)}
                  onClick={handleCloseSideBar}
                >
                  <Link
                    to="/users"
                    className={`cta-link ${
                      pathname.includes('users') && 'active'
                    }`}
                  >
                    <i>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.9">
                          <path
                            d="M19 5V7H15V5H19ZM9 5V11H5V5H9ZM19 13V19H15V13H19ZM9 17V19H5V17H9ZM21 3H13V9H21V3ZM11 3H3V13H11V3ZM21 11H13V21H21V11ZM11 15H3V21H11V15Z"
                            fill="#A8B0B9"
                          />
                        </g>
                      </svg>
                    </i>
                    <span>Agents</span>
                  </Link>
                </li>
                <li onClick={handleCloseSideBar}>
                  <Link
                    to="/billing"
                    className={`cta-link ${
                      pathname === '/billing' && 'active'
                    }`}
                  >
                    <i>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.9">
                          <path
                            d="M21 3H3C1.9 3 1 3.9 1 5V19C1 20.1 1.9 21 3 21H21C22.1 21 23 20.1 23 19V5C23 3.9 22.1 3 21 3ZM21 19H3V5H13V9H21V19Z"
                            fill="#A8B0B9"
                          />
                        </g>
                      </svg>
                    </i>
                    <span>Billing</span>
                  </Link>
                </li>
              </ul>
              <ul className="stickyFooter">
                <li onClick={handleCloseSideBar}>
                  <Link
                    to={`/our-work`}
                    className={`cta-link ${
                      pathname === '/our-work' && 'active'
                    }`}
                  >
                    <i>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3ZM1 12C1 5.92487 5.92487 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23C5.92487 23 1 18.0751 1 12Z"
                          fill="#A8B0B9"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M16.9469 7.05269C17.2147 7.32052 17.3083 7.71669 17.1885 8.07602L15.0685 14.436C14.9689 14.7346 14.7346 14.9689 14.436 15.0685L8.07602 17.1885C7.71669 17.3083 7.32052 17.2147 7.05269 16.9469C6.78485 16.6791 6.69133 16.2829 6.81111 15.9236L8.93111 9.56356C9.03064 9.26496 9.26496 9.03064 9.56356 8.93111L15.9236 6.81111C16.2829 6.69133 16.6791 6.78485 16.9469 7.05269ZM10.6704 10.6704L9.34093 14.6587L13.3292 13.3292L14.6587 9.34093L10.6704 10.6704Z"
                          fill="#A8B0B9"
                        />
                        <path
                          d="M10.6704 10.6704L9.34093 14.6587L13.3292 13.3292L10.6704 10.6704Z"
                          fill="#A8B0B9"
                        />
                      </svg>
                    </i>
                    <span>Our Work</span>
                  </Link>
                </li>
                <li onClick={handleCloseSideBar}>
                  <Link
                    to={`/profile/${user.id}/edit`}
                    className={`cta-link ${
                      pathname.includes('/profile/') && 'active'
                    }`}
                  >
                    <i>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.9">
                          <path
                            d="M19.4298 12.98C19.4698 12.66 19.4998 12.34 19.4998 12C19.4998 11.66 19.4698 11.34 19.4298 11.02L21.5398 9.37C21.7298 9.22 21.7798 8.95 21.6598 8.73L19.6598 5.27C19.5698 5.11 19.3998 5.02 19.2198 5.02C19.1598 5.02 19.0998 5.03 19.0498 5.05L16.5598 6.05C16.0398 5.65 15.4798 5.32 14.8698 5.07L14.4898 2.42C14.4598 2.18 14.2498 2 13.9998 2H9.99984C9.74984 2 9.53984 2.18 9.50984 2.42L9.12984 5.07C8.51984 5.32 7.95984 5.66 7.43984 6.05L4.94984 5.05C4.88984 5.03 4.82984 5.02 4.76984 5.02C4.59984 5.02 4.42984 5.11 4.33984 5.27L2.33984 8.73C2.20984 8.95 2.26984 9.22 2.45984 9.37L4.56984 11.02C4.52984 11.34 4.49984 11.67 4.49984 12C4.49984 12.33 4.52984 12.66 4.56984 12.98L2.45984 14.63C2.26984 14.78 2.21984 15.05 2.33984 15.27L4.33984 18.73C4.42984 18.89 4.59984 18.98 4.77984 18.98C4.83984 18.98 4.89984 18.97 4.94984 18.95L7.43984 17.95C7.95984 18.35 8.51984 18.68 9.12984 18.93L9.50984 21.58C9.53984 21.82 9.74984 22 9.99984 22H13.9998C14.2498 22 14.4598 21.82 14.4898 21.58L14.8698 18.93C15.4798 18.68 16.0398 18.34 16.5598 17.95L19.0498 18.95C19.1098 18.97 19.1698 18.98 19.2298 18.98C19.3998 18.98 19.5698 18.89 19.6598 18.73L21.6598 15.27C21.7798 15.05 21.7298 14.78 21.5398 14.63L19.4298 12.98ZM17.4498 11.27C17.4898 11.58 17.4998 11.79 17.4998 12C17.4998 12.21 17.4798 12.43 17.4498 12.73L17.3098 13.86L18.1998 14.56L19.2798 15.4L18.5798 16.61L17.3098 16.1L16.2698 15.68L15.3698 16.36C14.9398 16.68 14.5298 16.92 14.1198 17.09L13.0598 17.52L12.8998 18.65L12.6998 20H11.2998L11.1098 18.65L10.9498 17.52L9.88984 17.09C9.45984 16.91 9.05984 16.68 8.65984 16.38L7.74984 15.68L6.68984 16.11L5.41984 16.62L4.71984 15.41L5.79984 14.57L6.68984 13.87L6.54984 12.74C6.51984 12.43 6.49984 12.2 6.49984 12C6.49984 11.8 6.51984 11.57 6.54984 11.27L6.68984 10.14L5.79984 9.44L4.71984 8.6L5.41984 7.39L6.68984 7.9L7.72984 8.32L8.62984 7.64C9.05984 7.32 9.46984 7.08 9.87984 6.91L10.9398 6.48L11.0998 5.35L11.2998 4H12.6898L12.8798 5.35L13.0398 6.48L14.0998 6.91C14.5298 7.09 14.9298 7.32 15.3298 7.62L16.2398 8.32L17.2998 7.89L18.5698 7.38L19.2698 8.59L18.1998 9.44L17.3098 10.14L17.4498 11.27ZM11.9998 8C9.78984 8 7.99984 9.79 7.99984 12C7.99984 14.21 9.78984 16 11.9998 16C14.2098 16 15.9998 14.21 15.9998 12C15.9998 9.79 14.2098 8 11.9998 8ZM11.9998 14C10.8998 14 9.99984 13.1 9.99984 12C9.99984 10.9 10.8998 10 11.9998 10C13.0998 10 13.9998 10.9 13.9998 12C13.9998 13.1 13.0998 14 11.9998 14Z"
                            fill="#A8B0B9"
                          />
                        </g>
                      </svg>
                    </i>
                    <span>Settings</span>
                  </Link>
                </li>
                <li onClick={handleCloseSideBar}>
                  <Link hidden to="/help-center" className="cta-link">
                    <i>
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.9">
                          <path
                            d="M11 18H13V16H11V18ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12 6C9.79 6 8 7.79 8 10H10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 12 11 11.75 11 15H13C13 12.75 16 12.5 16 10C16 7.79 14.21 6 12 6Z"
                            fill="#A8B0B9"
                          />
                        </g>
                      </svg>
                    </i>
                    <span>Help center</span>
                  </Link>
                </li>
                <li onClick={handleLogout}>
                  <div className="cta-link">
                    <i>
                      <img src={LogoutIcon} alt="logout" />
                    </i>
                    <span className="logout-text">Log Out</span>
                  </div>
                </li>
              </ul>
              <div
                onClick={() => navigate(`/profile/${user.id}/edit`)}
                className="user"
              >
                <div className="avatar">
                  {user?.image ? (
                    <img
                      src={`${image_base_url}${user.image.replace(
                        '/storage',
                        ''
                      )}`}
                      alt="user avatar"
                      className="img-fluid"
                    />
                  ) : (
                    <AgentNoImage name={user?.name} surname={user?.last_name} />
                  )}
                </div>
                <div className="media-body">
                  <div className="name">
                    {user?.name} {user?.last_name}
                  </div>
                  <div className="desig">{user.position}</div>
                </div>
              </div>
              <div className="copyrights">
                © Copyright 2022 by <strong>PV Apps.</strong> <br />
                All rights reserved.
              </div>
            </div>
          )}
        {children}
      </div>
    </>
  );
}
